import { Badge, Box, Button, Flex, HStack, Icon, Stack, Text, useToast } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiFileText, FiSend } from "react-icons/fi";
import {
  AwardLetterDto,
  AwardLetterStatusDto,
  AwardLetterTypeDto,
  EvalBidDto,
  SourcingEventV2Dto,
  useDispatchAwardLettersMutation,
  useGetAwardLettersQuery,
  useGetEvaluationQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { AwardLetterEditor } from "./AwardLetterEditor";
import {
  AwardLetterDraftBidSummary,
  AwardLetterDraftCriterion,
  AwardLetterDraftInput,
} from "./useAwardLetterDraft";

export const AwardLetterWorkbench = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const authState = useLoggedInWithOrgContextState();
  const { data: lettersData } = useGetAwardLettersQuery({ eventId: event.id });
  const { data: evaluation } = useGetEvaluationQuery({ eventId: event.id });
  const [dispatchAll, { isLoading: isDispatching }] = useDispatchAwardLettersMutation();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const letters = useMemo(() => {
    const all = lettersData?.letters ?? [];
    return [...all].sort((a, b) => (a.type === AwardLetterTypeDto.Winner ? -1 : b.type === AwardLetterTypeDto.Winner ? 1 : 0));
  }, [lettersData]);

  // Criterion descriptions live on the event's award criteria; merge them onto the evaluation's
  // (non-price) criteria, which already carry the resolved weightPercent.
  const criteria: AwardLetterDraftCriterion[] = useMemo(() => {
    const descriptions = new Map<string, string>();
    const eventCriteria = event.eventData.awardCriteria?.bestPriceQualityRatio?.criteria ?? [];
    for (const c of eventCriteria) {
      if (c.id && c.description) descriptions.set(c.id, c.description);
    }
    return (evaluation?.criteria ?? [])
      .filter((c) => !c.isPrice)
      .map((c) => ({
        id: c.id,
        name: c.name?.trim() || t("Criterion") || "",
        weightPercent: c.weightPercent ?? 0,
        description: descriptions.get(c.id) ?? null,
      }));
  }, [evaluation, event.eventData.awardCriteria, t]);

  const winnerLetter = letters.find((l) => l.type === AwardLetterTypeDto.Winner);
  const winnerName = winnerLetter?.supplierName || t("the winner") || "";
  const winnerBid = useMemo(
    () => (winnerLetter ? evaluation?.bids.find((b) => b.bidId === winnerLetter.bidId) : undefined),
    [evaluation, winnerLetter],
  );

  const buildBidSummary = (
    supplierName: string,
    bid: EvalBidDto | undefined,
  ): AwardLetterDraftBidSummary => ({
    supplierName,
    responses: criteria.map((c) => {
      const cell = bid?.cells.find((cc) => cc.criterionId === c.id);
      return {
        criterionId: c.id,
        points: cell?.points ?? null,
        weightedPoints: cell?.weightedPoints ?? null,
        evaluatorComment: cell?.comment ?? null,
      };
    }),
    price:
      bid?.price && typeof bid.price.value === "number"
        ? { value: bid.price.value, currency: bid.price.currency }
        : null,
    rank: bid?.rank ?? null,
    weightedTotal: bid?.weightedTotal ?? null,
  });

  const aiInputFor = (letter: AwardLetterDto): AwardLetterDraftInput => {
    const recipientBid = evaluation?.bids.find((b) => b.bidId === letter.bidId);
    const isWinner = letter.type === AwardLetterTypeDto.Winner;
    const recipient = buildBidSummary(letter.supplierName || t("Unknown") || "", recipientBid);
    const winner = isWinner ? recipient : buildBidSummary(winnerName, winnerBid);
    return {
      letterType: isWinner ? "WINNER" : "REJECTION",
      eventTitle: event.eventData.title,
      eventValue:
        event.publicContext.estimatedValue && typeof event.publicContext.estimatedValue.value === "number"
          ? {
              value: event.publicContext.estimatedValue.value,
              currency: event.publicContext.estimatedValue.currency,
            }
          : null,
      criteria,
      recipient,
      winner,
    };
  };

  if (letters.length === 0) return null;

  const approvedCount = letters.filter(
    (l) => l.status === AwardLetterStatusDto.Approved || l.status === AwardLetterStatusDto.Sent,
  ).length;
  const allApproved = letters.every((l) => l.status === AwardLetterStatusDto.Approved);
  const isDispatched = letters.some((l) => l.status === AwardLetterStatusDto.Sent);
  const selected = letters.find((l) => l.id === selectedId);

  const onDispatch = async () => {
    try {
      await dispatchAll({ eventId: event.id }).unwrap();
      toast({ title: t("Award letters sent"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
      <Flex justify="space-between" align="center" mb="3" wrap="wrap" gap="2">
        <HStack spacing="2">
          <FiFileText />
          <Text fontWeight="semibold" color="smText">
            {t("Award letters")}
          </Text>
        </HStack>
        <Text fontSize="sm" color="smMuted">
          {t("{{approved}} of {{total}} approved", { approved: approvedCount, total: letters.length })}
        </Text>
      </Flex>

      <Stack spacing="2" mb="4">
        {letters.map((letter) => {
          const done = letter.status === AwardLetterStatusDto.Approved || letter.status === AwardLetterStatusDto.Sent;
          return (
            <Flex
              key={letter.id}
              as="button"
              onClick={() => setSelectedId(letter.id)}
              justify="space-between"
              align="center"
              bg="smBackgroundSecondary"
              rounded="lg"
              border="1px solid"
              borderColor="smBorder"
              px="4"
              py="3"
              textAlign="left"
              _hover={{ borderColor: "smPrimary" }}
            >
              <HStack spacing="3">
                <Icon as={done ? FiCheckCircle : FiFileText} color={done ? "smSuccess" : "smMuted"} />
                <Box>
                  <Text fontWeight="medium" color="smText">
                    {letter.supplierName || t("Unknown")}
                  </Text>
                  <Text fontSize="xs" color="smMuted">
                    {letter.type === AwardLetterTypeDto.Winner ? t("Winner letter") : t("Rejection letter")}
                  </Text>
                </Box>
              </HStack>
              <Badge colorScheme={statusColor(letter.status)}>{statusLabel(letter.status, t)}</Badge>
            </Flex>
          );
        })}
      </Stack>

      {isDispatched ? (
        <Flex
          align="center"
          gap="2"
          bg="smSuccessSoft"
          color="smSuccess"
          border="1px solid"
          borderColor="smSuccessSoftBorder"
          rounded="md"
          px="4"
          py="3"
        >
          <FiCheckCircle />
          <Text fontSize="sm">{t("Award letters have been sent to the suppliers.")}</Text>
        </Flex>
      ) : (
        <Stack spacing="2">
          <Button
            leftIcon={<FiSend />}
            colorScheme="teal"
            alignSelf="flex-start"
            onClick={onDispatch}
            isLoading={isDispatching}
            isDisabled={!allApproved}
          >
            {t("Send to all suppliers")}
          </Button>
          {!allApproved && (
            <Text fontSize="xs" color="smMuted">
              {t("All letters must be approved before they can be sent.")}
            </Text>
          )}
        </Stack>
      )}

      {selected && (
        <AwardLetterEditor
          key={selected.id}
          eventId={event.id}
          letter={selected}
          aiInput={aiInputFor(selected)}
          winnerName={winnerName}
          eventTitle={event.eventData.title}
          senderName={authState.selectedOrg.name}
          isOpen={!!selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </Box>
  );
};

const statusColor = (status: AwardLetterStatusDto): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return "green";
    case AwardLetterStatusDto.Approved:
      return "teal";
    default:
      return "gray";
  }
};

const statusLabel = (status: AwardLetterStatusDto, t: (k: string) => string): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return t("Sent");
    case AwardLetterStatusDto.Approved:
      return t("Approved");
    default:
      return t("Draft");
  }
};
